import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { WalletsQueryKeys } from '../../queryKeys';
import { UseWalletsMeProps } from './props';
import { fetchWalletsMe } from './service';
import { GenericError, Wallet } from '@starsoft/common/models';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { AuthService } from '@starsoft/common/services';

/**
 * Custom hook to fetch the current user's wallet information
 *
 * This hook handles fetching the authenticated user's wallet data,
 * managing the query state using React Query. The data is considered
 * fresh indefinitely (staleTime: Infinity) and only fetches when
 * the user is authorized.
 *
 * @param {UseWalletsMeProps} options - Optional configuration object
 * @param {boolean} [options.enabled=true] - Whether the query should be enabled
 * @param {boolean} [options.refetchOnMount=true] - Whether to refetch when component mounts
 * @param {boolean} [options.refetchOnWindowFocus=true] - Whether to refetch when window regains focus
 *
 * @returns {Object} An object containing:
 *   - isLoading: Boolean indicating if fetch is in progress (excluding idle state)
 *   - error: Any error that occurred during fetch
 *   - wallet: The fetched wallet data
 *   - refetch: Function to manually trigger a refetch
 *   - isRefetching: Boolean indicating if a refetch is in progress
 */
export function useWalletsMe(options?: UseWalletsMeProps) {
  const axiosInstance: AxiosInstance = useAxiosInstance();
  const {
    enabled = true,
    refetchOnMount = true,
    refetchOnWindowFocus = true,
  } = options ?? {};

  const { isPending, error, data, refetch, fetchStatus, isRefetching } =
    useQuery<Wallet, AxiosError<GenericError>>({
      queryKey: [WalletsQueryKeys.Me],
      queryFn: () => fetchWalletsMe(axiosInstance),
      enabled: enabled && AuthService.isAuthorized(),
      staleTime: Infinity,
      refetchOnMount,
      refetchOnWindowFocus,
    });

  return {
    isLoading: isPending && fetchStatus !== 'idle',
    error: error,
    wallet: data,
    refetch,
    isRefetching,
  };
}
